import { FormControl, MenuItem, Paper, Stack } from "@mui/material";
import { useTranslations } from "next-intl";
import React, { useCallback, useEffect } from "react";
import {
  CustomSelectComponent,
  CustomTextField,
  CustomToggleCompoment,
} from "../../../ui/input";
import { CustomInputLabel, CustomTypographyComponent } from "../../../ui/text";
import debounce from "lodash.debounce";
import { TextfieldFormSchema, TextfieldUiSchema } from "../types";

interface StateValue {
  formSchema: TextfieldFormSchema;
  uiSchema: TextfieldUiSchema;
  name: string;
}

interface FormBuilderPropertiesViewProps {
  inputPropertyData: any;
  onFormStateChange: (formState) => void;
}

const FormTextFieldViewer = ({
  inputPropertyData,
  onFormStateChange,
}: FormBuilderPropertiesViewProps) => {
  const [formPropertiesState, setFormPropertiesState] =
    React.useState<StateValue>(null);

  const t = useTranslations();

  useEffect(() => {
    setFormPropertiesState({
      ...inputPropertyData,
    });
  }, [inputPropertyData]);

  const debouncedSave = useCallback(
    debounce((nextValue) => onFormStateChange(nextValue), 1000),
    [onFormStateChange] // will be created only once initially
  );

  const handleChangeFormSchema =
    (prop: keyof StateValue["formSchema"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFormPropertiesState((prevState: StateValue) => {
        debouncedSave({
          ...prevState,
          formSchema: { ...prevState.formSchema, [prop]: event.target.value },
        });
        return {
          ...prevState,
          formSchema: { ...prevState.formSchema, [prop]: event.target.value },
        };
      });
    };

  const giveOneOfCOnst = (index: number) => {
    if (index === 0) {
      return "true";
    } else if (index === 2) {
      return "false";
    } else {
      return "na";
    }
  };

  const handlOneOFUpdate = (
    event: React.ChangeEvent<HTMLInputElement>,
    oneOfIndex: number
  ) => {
    const oneOfValueArray = formPropertiesState.formSchema.oneOf || [];

    const newValue = {
      const: giveOneOfCOnst(oneOfIndex),
      title: event.target.value,
    };
    oneOfValueArray[oneOfIndex] = newValue;
    setFormPropertiesState((prevState: StateValue) => {
      debouncedSave({
        ...prevState,
        formSchema: { ...prevState.formSchema, oneOf: oneOfValueArray },
      });
      return {
        ...prevState,
        formSchema: { ...prevState.formSchema, oneOf: oneOfValueArray },
      };
    });
  };

  const handleUiWidget = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormPropertiesState((prevState: StateValue) => {
      debouncedSave({
        ...prevState,
        uiSchema: { ...prevState.uiSchema, "ui:widget": event.target.value },
      });
      return {
        ...prevState,
        uiSchema: { ...prevState.uiSchema, "ui:widget": event.target.value },
      };
    });
  };

  const handleChangeUISchema =
    (prop: keyof StateValue["uiSchema"]["ui:options"]) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const prevUiOptions =
        (formPropertiesState.uiSchema &&
          formPropertiesState.uiSchema["ui:options"]) ||
        null;
      const fieldValue =
        event.target.value === "on" || event.target.value === "off"
          ? event.target.checked
          : event.target.value;
      //@ts-ignore
      setFormPropertiesState((prevState: StateValue) => {
        debouncedSave({
          ...prevState,
          uiSchema: {
            ...prevState.uiSchema,

            "ui:options": {
              ...prevUiOptions,
              [prop]: fieldValue,
            },
          },
        });
        return {
          ...prevState,
          uiSchema: {
            ...prevState.uiSchema,
            "ui:options": {
              ...prevUiOptions,
              [prop]: fieldValue,
            },
          },
        };
      });
    };

  const handleRenderType = (item) => (
    <Stack
      padding={3}
      spacing={2}
      direction="column"
      // height={"280"}
      width={320}
    >
      <FormControl sx={{ width: 300 }}>
        <CustomTextField
          controldisabled={false}
          id="outlined-adornment-title"
          value={
            (formPropertiesState && formPropertiesState.formSchema.title) || ""
          }
          label={t("labels.title")}
          onChange={handleChangeFormSchema("title")}
        />
      </FormControl>

      <FormControl sx={{ width: 300 }}>
        <CustomInputLabel id="format-select-label">
          {t("labels.format")}
        </CustomInputLabel>
        <CustomSelectComponent
          labelId="format-select-label"
          id="format-select"
          controldisabled={false}
          size="small"
          value={
            (formPropertiesState && formPropertiesState.formSchema.format) || ""
          }
          label={t("labels.format")}
          onChange={handleChangeFormSchema("format")}
        >
          <MenuItem value={""}>{t("labels.default")}</MenuItem>
          <MenuItem value={"date"}>{t("labels.date")}</MenuItem>
        </CustomSelectComponent>
      </FormControl>

      {formPropertiesState &&
        formPropertiesState.uiSchema &&
        formPropertiesState.uiSchema["ui:widget"] ===
          "ThreeSegmentedButtonsWidget" && (
          <FormControl sx={{ width: 300 }}>
            <CustomTextField
              controldisabled={false}
              id="outlined-adornment-oneOfTrueLabel"
              value={
                (formPropertiesState &&
                  formPropertiesState.formSchema.oneOf &&
                  formPropertiesState.formSchema.oneOf[0] &&
                  formPropertiesState.formSchema.oneOf[0].title) ||
                ""
              }
              label={t("labels.oneOfTrueLabel")}
              onChange={(event: any) => handlOneOFUpdate(event, 0)}
            />
          </FormControl>
        )}

      {formPropertiesState &&
        formPropertiesState.uiSchema &&
        formPropertiesState.uiSchema["ui:widget"] ===
          "ThreeSegmentedButtonsWidget" && (
          <FormControl sx={{ width: 300 }}>
            <CustomTextField
              controldisabled={false}
              id="outlined-adornment-oneOfNVTLabel"
              value={
                (formPropertiesState &&
                  formPropertiesState.formSchema.oneOf &&
                  formPropertiesState.formSchema.oneOf[1] &&
                  formPropertiesState.formSchema.oneOf[1].title) ||
                ""
              }
              label={t("labels.oneOfNVTLabel")}
              onChange={(event: any) => handlOneOFUpdate(event, 1)}
            />
          </FormControl>
        )}
      {formPropertiesState &&
        formPropertiesState.uiSchema &&
        formPropertiesState.uiSchema["ui:widget"] ===
          "ThreeSegmentedButtonsWidget" && (
          <FormControl sx={{ width: 300 }}>
            <CustomTextField
              controldisabled={false}
              id="outlined-adornment-oneOfFalseLabel"
              value={
                (formPropertiesState &&
                  formPropertiesState.formSchema.oneOf &&
                  formPropertiesState.formSchema.oneOf[2] &&
                  formPropertiesState.formSchema.oneOf[2].title) ||
                ""
              }
              label={t("labels.oneOfFalseLabel")}
              onChange={(event: any) => handlOneOFUpdate(event, 2)}
            />
          </FormControl>
        )}
    </Stack>
  );

  const renderUIOptions = (item) => (
    <Stack
      padding={3}
      spacing={2}
      direction="column"
      minHeight={250}
      width={300}
    >
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("fullWidth")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].fullWidth) ||
            false
          }
          label={t("labels.fullWidth")}
          name={t("labels.fullWidth")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("newLine")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].newLine) ||
            false
          }
          label={t("labels.newLine")}
          name={t("labels.newLine")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("autofocus")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].autofocus) ||
            false
          }
          label={t("labels.autofocus")}
          name={t("labels.autofocus")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("hideInApp")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].hideInApp) ||
            false
          }
          label={t("labels.hideInApp")}
          name={t("lables.hideInApp")}
        />
      </FormControl>
      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("inline")}
          checked={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].inline) ||
            false
          }
          label={t("labels.inline")}
          name={t("labels.inline")}
        />
      </FormControl>

      <FormControl sx={{ width: 300 }}>
        <CustomToggleCompoment
          controldisabled={false}
          onChange={handleChangeUISchema("defaultDate")}
          checked={
            (formPropertiesState &&
              formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].defaultDate) ||
            false
          }
          label={t("labels.defaultDate")}
          name={t("lables.defaultDate")}
        />
      </FormControl>

      <FormControl sx={{ width: 300 }}>
        <CustomTextField
          controldisabled={false}
          id="outlined-adornment-uiOptions-rows"
          value={
            (formPropertiesState &&
              formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:options"] &&
              formPropertiesState.uiSchema["ui:options"].rows) ||
            undefined
          }
          type="number"
          label={t("labels.rows")}
          onChange={handleChangeUISchema("rows")}
        />
      </FormControl>

      <FormControl sx={{ width: 300 }}>
        <CustomInputLabel id="widget-select-format">
          {t("labels.uiWidget")}
        </CustomInputLabel>
        <CustomSelectComponent
          labelId="widget-select-label"
          id="widget-select"
          controldisabled={false}
          size="small"
          value={
            (formPropertiesState.uiSchema &&
              formPropertiesState.uiSchema["ui:widget"]) ||
            ""
          }
          label={t("labels.uiWidget")}
          onChange={handleUiWidget}
        >
          <MenuItem value={"textarea"}>{t("labels.textarea")}</MenuItem>
          <MenuItem value={""}>{t("labels.default")}</MenuItem>
          <MenuItem value={"ThreeSegmentedButtonsWidget"}>
            {t("labels.ThreeSegmentedButtonsWidget")}
          </MenuItem>
          <MenuItem value={"ThreeSegmentedIconButtonsWidget"}>
            {t("labels.ThreeSegmentedIconButtonsWidget")}
          </MenuItem>
        </CustomSelectComponent>
      </FormControl>
    </Stack>
  );

  return (
    <Stack id="FormBuilderPropertiesView">
      <Paper>
        <Stack spacing={2}>
          <Stack mb={3}>
            <Stack alignItems={"center"}>
              <CustomTypographyComponent variant="h6">
                {t("labels.formSchema")}
              </CustomTypographyComponent>
              <CustomTypographyComponent variant="h6">
                {formPropertiesState && formPropertiesState.name}
              </CustomTypographyComponent>
            </Stack>
            {formPropertiesState && handleRenderType(formPropertiesState)}
          </Stack>
          <Stack>
            <Stack mt={10} alignItems={"center"}>
              <CustomTypographyComponent variant="h6">
                {t("labe;s.uiSchema")}
              </CustomTypographyComponent>
            </Stack>
            <Stack alignItems={"center"}>
              <CustomTypographyComponent variant="body2">
                {t("labels.uiOptions")}
              </CustomTypographyComponent>
            </Stack>

            {formPropertiesState && renderUIOptions(formPropertiesState)}
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default FormTextFieldViewer;
